import { Button, CircularProgress, makeStyles, Typography } from '@material-ui/core';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CollapsableCard from 'components/Generic/CollapsableCard';
import { notify } from 'components/Notifier';
import { AppContext } from 'context';
import YearMonthPeriod from 'components/YearMonthPeriod';
import { Period } from 'types/Period';
import { ReportDownloadStatus } from 'enums/reports';
import { ClosedMonthsBoundaries } from 'types';
import { periodIsFullOfClosedMonths } from 'services/closure';

const useStyles = makeStyles(() => ({
  infoDisplay: {
    width: '80%',
  },
  collapsableContent: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  periodInputField: {
    width: '45%',
    marginLeft: '0px',

    '& input': {
      cursor: 'pointer',
    },
    '& label': {
      color: '#949FAC',
    },
  },
  downloadButton: {
    marginRight: '30px',
  },
}));

type DownloadProductionExpeditionReportCardProps = {
  collapseTitle: string;
  closedMonthsBoundaries: ClosedMonthsBoundaries | null;
  handleClickDownload: (sortingCenterId: string, startDate: Date, endDate: Date) => Promise<void>;
};

const DownloadProductionExpeditionReportCard: React.FC<DownloadProductionExpeditionReportCardProps> = ({
  collapseTitle,
  handleClickDownload,
  closedMonthsBoundaries,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.() ?? null;
  const sortingCenterId = sortingCenter?.['@id'] ?? null;

  const [downloadStatus, setDownloadStatus] = useState<ReportDownloadStatus>(ReportDownloadStatus.INITIAL);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [minStartDate, setMinStartDate] = useState<Date>(new Date());
  const [startDateError, setStartDateError] = useState(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [maxEndDate, setMaxEndDate] = useState<Date>(new Date());
  const [endDateError, setEndDateError] = useState(null);

  useEffect(() => {
    const minStartDate = closedMonthsBoundaries
      ? closedMonthsBoundaries.firstClosureDate
      : sortingCenter
      ? new Date(sortingCenter.initializationDate)
      : new Date();
    const maxEndDate = closedMonthsBoundaries ? closedMonthsBoundaries.lastClosureDate : new Date();

    setMinStartDate(minStartDate);
    setMaxEndDate(maxEndDate);
  }, [closedMonthsBoundaries, sortingCenter]);

  const onClickDownload = async () => {
    if (!sortingCenterId) return;

    setStartDateError(null);
    setEndDateError(null);

    if (!startDate || !endDate) {
      setStartDateError(!startDate ? t('reports.validation.required') : null);
      setEndDateError(!endDate ? t('reports.validation.required') : null);
      return;
    } else if (
      !closedMonthsBoundaries ||
      (closedMonthsBoundaries && !periodIsFullOfClosedMonths(closedMonthsBoundaries, startDate, endDate))
    ) {
      setDownloadStatus(ReportDownloadStatus.NOT_PROCESSABLE);
      return;
    }

    setDownloadStatus(ReportDownloadStatus.PENDING);

    handleClickDownload(sortingCenterId, startDate, endDate)
      .then(() => {
        setDownloadStatus(ReportDownloadStatus.SUCCESS);
        notify({
          message: t('reports.download.success.long'),
          severity: 'success',
        });
      })
      .catch(() => {
        setDownloadStatus(ReportDownloadStatus.ERROR);
        notify({
          message: t('reports.download.error'),
          severity: 'error',
        });
      });
  };

  const onChangePeriod = (period: Period) => {
    if (undefined === period.startDate) period.startDate = null;
    if (undefined === period.endDate) period.endDate = null;

    setStartDate(period.startDate);
    setEndDate(period.endDate);
  };

  return (
    <CollapsableCard
      maxWidth="942px"
      collapsableContent={
        <div className={classes.collapsableContent}>
          <div className={classes.periodInputField}>
            <YearMonthPeriod
              defaultValues={{
                startDate,
                endDate,
              }}
              minStartDate={minStartDate}
              maxEndDate={maxEndDate}
              onChange={onChangePeriod}
              startDateError={startDateError}
              endDateError={endDateError}
            />
          </div>
          <div>
            {downloadStatus === ReportDownloadStatus.NOT_PROCESSABLE && (
              <Typography color="error">{t('reports.download.nonClosedMonth')}</Typography>
            )}
            {downloadStatus === ReportDownloadStatus.SUCCESS && (
              <Typography color="primary">{t('reports.download.success.short')}</Typography>
            )}
            {downloadStatus === ReportDownloadStatus.PENDING && <CircularProgress size={25} />}
          </div>
          <div>
            <Button
              size="small"
              variant="outlined"
              className={classes.downloadButton}
              onClick={onClickDownload}
              disabled={downloadStatus === ReportDownloadStatus.PENDING}
            >
              {t('reports.button.download')}
            </Button>
          </div>
        </div>
      }
    >
      <Typography className={classes.infoDisplay}>{collapseTitle}</Typography>
    </CollapsableCard>
  );
};
export default DownloadProductionExpeditionReportCard;
